import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

export function parseYouTubeId(input) {
  if (!input) return "";
  const s = String(input).trim();
  if (/^[\w-]{11}$/.test(s)) return s;
  const m = s.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : "";
}

export const CORNER_LAPTOP_DEFAULTS = {
  enabled: false,
  hide_mobile: true,
  z_index: 30,
  offset_x: 20,
  offset_y: 20,
  width: 320,
  link: "",
  float_anim: true,
  screen_popup_enabled: true,
  // frame
  lid_color: "#0EA5E9",
  base_color: "#0B1D3A",
  bezel_color: "#0B1D3A",
  bezel_width: 9,
  screen_radius: 6,
  shadow: "strong",
  glow: false,
  glow_color: "#0EA5E9",
  // screen content
  screen_image: "",
  screen_image_en: "",
  screen_video: "",
  screen_video_en: "",
  screen_youtube: "",
  screen_youtube_en: "",
  screen_fit: "cover",
  screen_bg1: "#0EA5E9",
  screen_bg2: "#6366F1",
  screen_brightness: 100,
  screen_contrast: 100,
  screen_saturate: 100,
  screen_overlay_color: "#000000",
  screen_overlay_opacity: 0,
  screen_overlay_blend: "normal",
  // tag / speech bubble
  tag_enabled: true,
  tag_pt: "EXPERIMENTA O JOGO!",
  tag_en: "TRY THE GAME!",
  tag_bg: "#F97316",
  tag_color: "#FFFFFF",
  tag_border_color: "#FFFFFF",
  tag_border_width: 2,
  tag_font: "luckiest",
  tag_size: 22,
  tag_radius: 18,
  tag_tilt: -3,
  tag_pos: "top_right",
  tag_effect: "none",
  tag_offset_x: 0,
  tag_offset_y: 0,
  // view / perspective
  card_view: "real3d",
  // real 3d controls (used when card_view === "real3d")
  scene_rx: -4,
  scene_ry: -18,
  scene_rz: 0,
  screen_lean: 8,
  base_tilt: 66,
  persp: 1500,
};

// Flat (2D) perspective presets, same set as the Promo Card.
const VIEWS = {
  flat: undefined,
  persp_left: "perspective(800px) rotateY(14deg)",
  persp_right: "perspective(800px) rotateY(-14deg)",
  persp_left_strong: "perspective(700px) rotateY(26deg)",
  persp_right_strong: "perspective(700px) rotateY(-26deg)",
  tilt_up: "perspective(800px) rotateX(12deg)",
  tilt_down: "perspective(800px) rotateX(-12deg)",
  iso: "perspective(900px) rotateX(8deg) rotateY(-12deg)",
  iso_right: "perspective(900px) rotateX(8deg) rotateY(12deg)",
  corner_tl: "perspective(900px) rotateX(10deg) rotateY(14deg)",
  corner_tr: "perspective(900px) rotateX(10deg) rotateY(-14deg)",
  corner_bl: "perspective(900px) rotateX(-10deg) rotateY(14deg)",
  corner_br: "perspective(900px) rotateX(-10deg) rotateY(-14deg)",
  book_left: "perspective(600px) rotateY(38deg)",
  book_right: "perspective(600px) rotateY(-38deg)",
  float: "perspective(1200px) rotateX(5deg)",
};

const SHADOWS = {
  none: "none",
  soft: "0 12px 24px rgba(0,0,0,0.22)",
  medium: "0 22px 44px rgba(0,0,0,0.38)",
  strong: "0 34px 70px rgba(0,0,0,0.55)",
};

export default function CornerLaptop({ config, lang = "pt", backendUrl, preview = false, inline = false }) {
  const navigate = useNavigate();
  const [popupOpen, setPopupOpen] = useState(false);
  const c = { ...CORNER_LAPTOP_DEFAULTS, ...(config || {}) };
  if (!preview && c.enabled === false) return null;

  const W = Math.max(140, Number(c.width) || 320);
  const screenH = Math.round(W * 0.625);
  const bezel = Math.max(0, Number(c.bezel_width) || 0);
  const rad = Math.max(0, Number(c.screen_radius) || 0);
  const screenUnitH = screenH + 2 * bezel + 10;

  const imgPath = lang === "en" && c.screen_image_en ? c.screen_image_en : c.screen_image;
  const imgUrl = imgPath ? `${backendUrl}/api/files/${imgPath}` : null;
  const videoPath = lang === "en" && c.screen_video_en ? c.screen_video_en : c.screen_video;
  const videoUrl = videoPath ? `${backendUrl}/api/files/${videoPath}` : null;
  const ytRaw = lang === "en" && c.screen_youtube_en ? c.screen_youtube_en : c.screen_youtube;
  const ytId = parseYouTubeId(ytRaw);
  const screenBg = `linear-gradient(150deg, ${c.screen_bg1 || "#1E3A8A"} 0%, ${c.screen_bg2 || "#3B82F6"} 100%)`;
  const screenFilter = `brightness(${(Number(c.screen_brightness) ?? 100) / 100}) contrast(${(Number(c.screen_contrast) ?? 100) / 100}) saturate(${(Number(c.screen_saturate) ?? 100) / 100})`;

  const tagText = lang === "en" ? (c.tag_en || c.tag_pt) : c.tag_pt;
  const tagSize = Number(c.tag_size) || 22;
  const tail = Math.max(12, Math.round(tagSize * 0.7));
  const is3d = c.card_view === "real3d";

  const hasVideo = !!(ytId || videoUrl);
  const popupOn = c.screen_popup_enabled !== false && hasVideo;

  const handleClick = () => {
    if (preview) return;
    if (popupOn) { setPopupOpen(true); return; }
    if (!c.link) return;
    if (/^https?:\/\//.test(c.link)) window.open(c.link, "_blank", "noopener");
    else navigate(c.link);
  };
  const clickable = !preview && (popupOn || !!c.link);

  // ---- Tag / speech bubble ----
  const TAG_POS = {
    top_right: { right: "-4px", bottom: "86%" },
    top_left: { left: "-4px", bottom: "86%" },
    top_center: { left: "50%", bottom: "100%" },
  };
  const posStyle = TAG_POS[c.tag_pos] || TAG_POS.top_right;
  const tagTransforms = [];
  if (c.tag_pos === "top_center") tagTransforms.push("translateX(-50%)");
  tagTransforms.push(`translate(${Number(c.tag_offset_x) || 0}px, ${-(Number(c.tag_offset_y) || 0)}px)`);
  if (c.tag_tilt) tagTransforms.push(`rotate(${c.tag_tilt}deg)`);
  const tailLeft = c.tag_pos === "top_left" ? "22%" : c.tag_pos === "top_center" ? "50%" : "72%";
  const tagBorder = (Number(c.tag_border_width) || 0) > 0 ? `${c.tag_border_width}px solid ${c.tag_border_color || "#FFFFFF"}` : "none";
  const tagFxClass = c.tag_effect && c.tag_effect !== "none" ? `promo-card-fx-${c.tag_effect}` : "";

  const tag = c.tag_enabled !== false && tagText ? (
    <div className={`absolute z-20 ${tagFxClass}`} data-testid="corner-laptop-tag"
      style={{ ...posStyle, transform: tagTransforms.join(" "), transformOrigin: "center bottom" }}>
      <div className="relative">
        <div className={`relative font-${c.tag_font || "luckiest"} font-extrabold leading-tight text-center whitespace-pre-line`}
          style={{
            background: c.tag_bg || "#1B8A3A", color: c.tag_color || "#FFFFFF",
            border: tagBorder, borderRadius: `${c.tag_radius ?? 18}px`,
            padding: "8px 16px", fontSize: `${tagSize}px`,
            boxShadow: "0 10px 24px rgba(0,0,0,0.30)", textShadow: "1px 1px 0 rgba(0,0,0,0.18)",
          }}>
          {tagText}
        </div>
        <div className="absolute" aria-hidden
          style={{
            width: tail, height: tail, left: tailLeft, bottom: -(tail / 2),
            transform: "translateX(-50%) rotate(45deg)",
            background: c.tag_bg || "#1B8A3A",
            borderRight: tagBorder, borderBottom: tagBorder,
            borderBottomRightRadius: "3px",
          }} />
      </div>
    </div>
  ) : null;

  // ---- Screen face (shared by flat + 3d) ----
  const screenFace = (
    <div style={{
      width: W, background: c.lid_color || "#C2C6CE",
      borderRadius: `${rad + 8}px`, padding: "5px",
      boxShadow: is3d ? "0 2px 6px rgba(0,0,0,0.35)" : (SHADOWS[c.shadow] || SHADOWS.strong),
      filter: c.glow ? `drop-shadow(0 0 14px ${c.glow_color || "#FFD93D"})` : undefined,
    }}>
      <div style={{ background: c.bezel_color || "#14171F", borderRadius: `${rad + 4}px`, padding: bezel, position: "relative" }}>
        <div style={{ position: "absolute", top: Math.max(2, bezel * 0.45), left: "50%", transform: "translateX(-50%)", width: 5, height: 5, borderRadius: "50%", background: "#3a3f48" }} />
        <div style={{ width: "100%", height: screenH, borderRadius: `${rad}px`, overflow: "hidden", background: screenBg, position: "relative" }}>
          {ytId ? (
            <iframe
              title="laptop-video"
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&loop=1&playlist=${ytId}&controls=0&modestbranding=1&playsinline=1&rel=0`}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, filter: screenFilter, pointerEvents: preview ? "none" : "auto" }}
            />
          ) : videoUrl ? (
            <video src={videoUrl} autoPlay muted loop playsInline
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: c.screen_fit || "cover", filter: screenFilter }} />
          ) : imgUrl ? (
            <img src={imgUrl} alt="" style={{
              position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: c.screen_fit || "cover",
              filter: screenFilter,
            }} />
          ) : null}
          {(Number(c.screen_overlay_opacity) || 0) > 0 && (
            <div style={{
              position: "absolute", inset: 0, pointerEvents: "none",
              background: c.screen_overlay_color || "#000000",
              opacity: (Number(c.screen_overlay_opacity) || 0) / 100,
              mixBlendMode: c.screen_overlay_blend || "normal",
            }} />
          )}
        </div>
      </div>
    </div>
  );

  let body;
  if (is3d) {
    const baseDepth = Math.round(screenH * 0.8);
    const baseW = Math.round(W * 1.02);
    const sceneW = Math.round(W * 1.25);
    const sceneH = Math.round((screenUnitH + baseDepth) * 1.05);
    const keys = "repeating-linear-gradient(90deg, rgba(0,0,0,0.55) 0 2px, transparent 2px 22px), repeating-linear-gradient(0deg, rgba(0,0,0,0.55) 0 2px, transparent 2px 22px)";
    body = (
      <div onClick={handleClick} style={{ width: sceneW, height: sceneH, perspective: `${c.persp ?? 1500}px`, cursor: clickable ? "pointer" : "default" }}>
        <div style={{ position: "relative", width: "100%", height: "100%", transformStyle: "preserve-3d", transform: `rotateX(${c.scene_rx ?? -4}deg) rotateY(${c.scene_ry ?? -18}deg) rotateZ(${c.scene_rz ?? 0}deg)` }}>
          {/* Screen panel (stands up, leans back) */}
          <div style={{
            position: "absolute", left: "50%", top: `${Math.round(sceneH * 0.04)}px`, width: W, height: screenUnitH,
            marginLeft: -W / 2, transformOrigin: "center bottom",
            transform: `rotateX(${-(c.screen_lean ?? 8)}deg)`,
            filter: c.glow ? `drop-shadow(0 0 16px ${c.glow_color || "#FFD93D"})` : "drop-shadow(0 18px 30px rgba(0,0,0,0.5))",
          }}>
            {screenFace}
          </div>
          {/* Keyboard base (lies down, recedes) */}
          <div style={{
            position: "absolute", left: "50%", top: `${Math.round(sceneH * 0.04) + screenUnitH}px`, width: baseW, height: baseDepth,
            marginLeft: -baseW / 2, transformOrigin: "center top",
            transform: `rotateX(${c.base_tilt ?? 66}deg)`,
            background: `linear-gradient(180deg, #FFFFFF 0%, ${c.base_color || "#AFB3BC"} 22%, ${c.base_color || "#AFB3BC"} 70%, rgba(0,0,0,0.35) 100%)`,
            borderRadius: "4px 4px 10px 10px",
            boxShadow: "0 30px 50px rgba(0,0,0,0.5)",
          }}>
            <div style={{ position: "absolute", inset: "12% 7% 30% 7%", borderRadius: 5, background: "rgba(0,0,0,0.18)", backgroundImage: keys, backgroundClip: "padding-box" }} />
            <div style={{ position: "absolute", bottom: "8%", left: "50%", transform: "translateX(-50%)", width: "30%", height: "16%", borderRadius: 4, background: "rgba(255,255,255,0.35)", boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.12)" }} />
          </div>
        </div>
      </div>
    );
  } else {
    const baseW = Math.round(W * 1.16);
    const baseH = Math.max(10, Math.round(W * 0.05));
    const hingeH = Math.max(5, Math.round(W * 0.016));
    body = (
      <div onClick={handleClick} style={{ cursor: clickable ? "pointer" : "default", transform: VIEWS[c.card_view || "flat"], transformStyle: VIEWS[c.card_view || "flat"] ? "preserve-3d" : undefined }}>
        {screenFace}
        <div style={{ width: W * 0.5, height: hingeH, margin: "0 auto", background: `linear-gradient(180deg, ${c.base_color || "#AFB3BC"}, rgba(0,0,0,0.25))`, borderRadius: "0 0 4px 4px" }} />
        <div style={{
          width: baseW, height: baseH, margin: "0 auto", position: "relative",
          background: `linear-gradient(180deg, #FFFFFF 0%, ${c.base_color || "#AFB3BC"} 38%, rgba(0,0,0,0.32) 100%)`,
          clipPath: "polygon(5% 0, 95% 0, 100% 100%, 0 100%)",
          boxShadow: "0 14px 22px rgba(0,0,0,0.30)",
        }}>
          <div style={{ position: "absolute", top: "16%", left: "50%", transform: "translateX(-50%)", width: "26%", height: "34%", borderRadius: "2px", background: "rgba(0,0,0,0.18)" }} />
        </div>
      </div>
    );
  }

  const assembly = (
    <div className="relative" style={{ width: is3d ? "auto" : Math.round(W * 1.16) }} data-testid="corner-laptop">
      {tag}
      {body}
    </div>
  );

  if (preview) return assembly;

  const wrapperFx = c.float_anim ? "promo-card-fx-float" : "";

  const popup = popupOpen ? (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      onClick={() => setPopupOpen(false)} data-testid="corner-laptop-popup">
      <button onClick={() => setPopupOpen(false)} aria-label="Fechar" data-testid="corner-laptop-popup-close"
        className="absolute top-5 right-5 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors text-2xl leading-none">×</button>
      <div className="w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black" onClick={(e) => e.stopPropagation()}>
        {ytId ? (
          <iframe title="laptop-video-popup"
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1&controls=1&modestbranding=1&playsinline=1&rel=0`}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen
            style={{ width: "100%", height: "100%", border: 0 }} />
        ) : videoUrl ? (
          <video src={videoUrl} autoPlay controls playsInline style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }} />
        ) : null}
      </div>
    </div>
  ) : null;

  if (inline) {
    return (
      <>
      <div className="w-full flex justify-center md:justify-end max-[1023px]:!-mb-32" data-testid="corner-laptop-wrap"
        style={{ marginTop: `${c.offset_y ?? 20}px` }}>
        <div className="origin-top scale-[0.8] sm:scale-90 md:scale-100">
          <div className={wrapperFx} style={{ transform: `translateX(${Number(c.offset_x) || 0}px)` }}>{assembly}</div>
        </div>
      </div>
      {popup}
      </>
    );
  }

  return (
    <>
    <div className={`fixed ${c.hide_mobile ? "hidden md:block" : "block"}`}
      style={{ right: `${c.offset_x ?? 20}px`, bottom: `${c.offset_y ?? 20}px`, zIndex: c.z_index ?? 30 }}
      data-testid="corner-laptop-wrap">
      <div className={wrapperFx}>{assembly}</div>
    </div>
    {popup}
    </>
  );
}
